import { cloneDeep } from '@graphcommerce/graphql'
import type {
  AggregationOption,
  FilterEqualTypeInput,
  FilterRangeTypeInput,
} from '@graphcommerce/graphql-mesh'
import { ProductListFiltersFragment } from '@graphcommerce/magento-product/components/ProductListFilters/ProductListFilters.gql'
import { useProductListLinkReplace } from '@graphcommerce/magento-product/hooks/useProductListLinkReplace'
import { useProductListParamsContext } from '@graphcommerce/magento-product/hooks/useProductListParamsContext'
import { ChipMenuProps } from '@graphcommerce/next-ui'
import { Checkbox } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Icon } from '../../Layout/Icon'

type FilterRangeTypeProps = NonNullable<
  NonNullable<ProductListFiltersFragment['aggregations']>[0]
> &
  Omit<ChipMenuProps, 'selected'>

export function FilterRangeAsOptionsType(props: FilterRangeTypeProps) {
  const { attribute_code, label, options } = props
  const { params } = useProductListParamsContext()
  const replaceRoute = useProductListLinkReplace({ scroll: false })
  const [isOpen, setIsOpen] = useState(true)

  // eslint-disable-next-line no-case-declarations
  const marks: { [index: number]: { value: number; label?: React.ReactNode } } = {}
  const paramValues = params.filters[attribute_code]

  const [min, maxish] = options
    ?.map((option) => {
      let val = option?.value.replace('*_', '0_') ?? ''
      val = val.replace('_*', '_0')
      const [minVal, maxVal] = val.split('_').map((value) => Number(value))

      marks[minVal] = { value: minVal, label: minVal }
      marks[maxVal] = { value: maxVal, label: maxVal }
      return [minVal, maxVal]
    })
    .reduce(([prevMin, prevMax], [curMin, curMax]) => [
      Math.min(prevMin, curMin),
      Math.max(curMax, prevMax),
    ]) ?? [0, 0]

  // eslint-disable-next-line no-case-declarations
  const max = (maxish / (options?.length ?? 2 - 1)) * (options?.length ?? 1)
  marks[max] = { value: max, label: max }

  const [value, setValue] = React.useState<[number, number]>(
    paramValues ? [Number(paramValues.from), Number(paramValues.to)] : [min, max],
  )

  useEffect(() => {
    if (!paramValues) setValue([min, max])
  }, [max, min, paramValues])

  const getUrl = (newValue: string) => {
    const range = newValue.split('-')
    const priceFilterUrl = cloneDeep(params)
    delete priceFilterUrl.currentPage
    priceFilterUrl.filters[attribute_code] = {
      from: String(range[0]),
      to: String(range[1]),
    } as FilterRangeTypeInput
    replaceRoute({ ...priceFilterUrl })
  }

  const resetFilter = () => {
    const linkParams = cloneDeep(params)

    delete linkParams.currentPage
    delete linkParams.filters[attribute_code]

    setValue([min, max])
    replaceRoute(linkParams)
  }

  const currentFilter = params.filters[attribute_code] as FilterRangeTypeInput | undefined

  let currentFilterAsEqualType = currentFilter as FilterEqualTypeInput

  if (currentFilterAsEqualType) {
    currentFilterAsEqualType = { in: [`${currentFilter?.from}-${currentFilter?.to}`] }
  } else {
    currentFilterAsEqualType = { in: [] }
  }

  const handleClick = (option: AggregationOption) => {
    if (option?.value.endsWith(`-${currentFilter?.to}`)) {
      resetFilter()
    } else {
      getUrl(option?.value)
    }
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        type='button'
        className='mb-[10px] inline-flex w-full items-center justify-center'
      >
        <div className='flex w-full items-center justify-between'>
          <span className='Type-XL-Medium'>{label}</span>
          <span className='inline-flex h-[20px] w-[20px] items-center justify-center'>
            {isOpen ? (
              <Icon name='expand_less' className='text-4xl' />
            ) : (
              <Icon name='expand_more' className='text-4xl' />
            )}
          </span>
        </div>
      </button>
      {isOpen && (
        <div className='mb-[10px] pl-[30px]'>
          {options?.map((option) => {
            if (!option?.value) return null
            const filters = cloneDeep(params.filters)

            if (currentFilterAsEqualType.in?.includes(option.value)) {
              filters[attribute_code] = {
                ...currentFilter,
                in: currentFilterAsEqualType.in?.filter((v) => v !== option.value),
              }
            } else {
              filters[attribute_code] = {
                ...currentFilter,
                in: [...(currentFilterAsEqualType?.in ?? []), option.value],
              }
            }

            return (
              <button
                type='button'
                key={option?.value}
                className='block'
                onClick={() => handleClick(option)}
              >
                <div className='flex items-center'>
                  <Checkbox checked={currentFilterAsEqualType.in?.includes(option?.value ?? '')} />
                  <p className='Type-XL-Regular text-left'>{option?.label}</p>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
